/**
 * This is a collection of tools for dealing with on-screen 2D representations
 * of 3D scenes in the browser. The tools use three.js utilities and scene
 * graph classes.
 */

import { lerp } from './math/lerp';
import {
    Box3,
    Camera,
    Object3D,
    OrthographicCamera,
    Vector2,
    Vector3,
} from 'three';

import logging from './logging';

const log = logging('view3d');

/**
 * This function takes an Event object and returns the normalized device
 * coordinates (ranging from -1 to 1 with positive Y in the up direction) of the
 * mouse position over the event's target (a 3D canvas). This is intended for
 * use in mouse event handlers where we have access to a real event object.
 * @function ndce
 * @param event - The mouse event that you're trying to handle; we need
 * access to the mouse position and the size of the event target, namely the
 * canvas. So this
 * @param vec - If you pass in a Vector2, then its x and y
 * properties will be updated; if the vec parameter is missing, then a new
 * Vector2 is created for you.
 * @returns This will be an update to the vec parameter if you
 * provided it or a new instance if the vec parameter was omitted. This means
 * you don't have to capture the output if you're updating vec.
 */
export const ndce = (event: MouseEvent, vec=null) => {
    const
        rect = (event.target as HTMLElement).getBoundingClientRect(),
        x = event.clientX - rect.left,
        y = event.clientY - rect.top;
    return ndc(x, y, rect.width, rect.height, vec);
}

/**
 * This is a more generic function to calculate normalized device coordinates
 * (ranging from -1 to 1 with positive Y in the up direction) for an X and Y
 * position in screen space relative to a rectangle with a given width and
 * height. The input is assumed to have its origin in the upper left with
 * positive Y increasing downwards, whereas in the output, the positive Y axis
 * points upwards.
 * @param x - The x position relative to the top left corner of the
 * screen rectangle.
 * @param y - The y position relative to the top left corner of the
 * screen rectangle where larger Y values are closer to the bottom.
 * @param width - The width of the screen rectangle, usually in pixels.
 * @param height - The height of the screen rectangle, usually in
 * pixels.
 * @returns vec - This will be an update to the vec parameter if
 * you provided it or a new instance if the vec parameter was omitted. This
 * means you don't have to capture the output if you're updating vec.
 */
export const ndc = (x:number, y:number, width:number, height:number, vec: Vector2 = null) => {
    if(vec == null){
        vec = new Vector2();
    }
    vec.x = (x / width) * 2 - 1;
    vec.y = -(y / height) * 2 + 1;
    return vec;
}

/**
 * This is the inverse of the ndc function, so it takes normalized device
 * coordinates and translates them into screen space.
 * @param normX - the x position in normalized device coordinates (-1
 * to 1)
 * @param normY - the x position in normalized device coordinates (-1
 * to 1)
 * @param width - the width of the screen rectangle, usually in pixels.
 * @param height - the height of the screen rectangle, usually in
 * pixels.
 * @param vec - This function will update the vec parameter if
 * you provided it. This means you don't have to capture the output if you're
 * updating vec.
 */
export const indc = (normX:number, normY:number, width:number, height:number, vec: Vector2 = null) => {
    if(vec == null){
        vec = new Vector2();
    }
    vec.x = ((normX+1)/2)*width;
    vec.y = -((normY-1)/2)*height;
    return vec;
}

/**
 * World to screen coordinate transform. This is helpful for getting the
 * screen position of single 3D points.
 * @param camera - the camera view you want to use to perform the
 * translation. Most likely this is the active camera.
 * @param worldVec - the position in world space; if you want
 * the position of an object that has a transformed parent, use
 * Object3D.localToWorld().
 * @param ndcVec - This is the output parameter; it can be
 * omitted if you just want a new vector, or you can supply a vector that the
 * value should be written into. Even though the goal is to get a screen space
 * result, we still have a Z component in the output, which is essentially the
 * distance from the camera. Just ignore it if you don't need it. Note that the
 * result is in normalized device coordinates that range from -1 to 1. See the
 * indc (Inverse NDC) function if you need screen coordinates in pixels.
 */
export const w2s = (camera: Camera, worldVec: Vector3, ndcVec: Vector3 = null) => {
    if(ndcVec == null){
        ndcVec = new Vector3();
    }
    ndcVec.copy(worldVec);
    ndcVec.project(camera);
    return ndcVec;
}

/**
 * Translate the screen coordinates of the mouse cursor (given by event) into
 * 2D world coordinates for the special ccase of an orthographic camera looking
 * down the Z axis. This only works for the special case since screen
 * coordinates are ambiguous with regard to depth. The result is in the world's
 * XY plane.
 */
export const s2w = (camera: OrthographicCamera, event: MouseEvent, outVec: Vector2 = null) => {
    if(outVec == null) {
        outVec = new Vector2();
    }
    const
        rect = (event.currentTarget as HTMLElement).getBoundingClientRect(),
        sx = (event.clientX - rect.left) / rect.width,
        // we want the complement of the y position so that we're getting the
        // mouse's upright coordinates
        sy = 1 - ((event.clientY - rect.top) / rect.height),
        left = camera.left / camera.zoom + camera.position.x,
        right = camera.right / camera.zoom + camera.position.x,
        bottom = camera.bottom / camera.zoom + camera.position.y,
        top = camera.top / camera.zoom + camera.position.y,
        wx = lerp(left, right, sx),
        wy = lerp(bottom, top, sy);
    //log.debug(`sx(${sx}) sy(${sy})`);
    //log.debug(`wx(${wx}) wy(${wy})`);
    //log.debug(`left(${camera.left}) right(${camera.right}) bottom(${camera.bottom}) top(${camera.top}) `);
    outVec.set(wx, wy);
    return outVec;
}

/**
 * Same as `s2w` but uses an explicit target instead of event.currentTarget.
 */
export const s2wElt = (camera: OrthographicCamera, event: MouseEvent, target: HTMLElement, outVec: Vector2 = null) => {
    if(outVec == null) {
        outVec = new Vector2();
    }
    const
        rect = target.getBoundingClientRect(),
        sx = (event.clientX - rect.left) / rect.width,
        // we want the complement of the y position so that we're getting the
        // mouse's upright coordinates
        sy = 1 - ((event.clientY - rect.top) / rect.height),
        left = camera.left / camera.zoom + camera.position.x,
        right = camera.right / camera.zoom + camera.position.x,
        bottom = camera.bottom / camera.zoom + camera.position.y,
        top = camera.top / camera.zoom + camera.position.y,
        wx = lerp(left, right, sx),
        wy = lerp(bottom, top, sy);
    //log.debug(`sx(${sx}) sy(${sy})`);
    //log.debug(`wx(${wx}) wy(${wy})`);
    //log.debug(`left(${camera.left}) right(${camera.right}) bottom(${camera.bottom}) top(${camera.top}) `);
    outVec.set(wx, wy);
    return outVec;
}

/**
 * Produces an "orthographic scale" factor that scales world distances to pixel
 * dimensions. This only works for orthographic cameras. The units of the scale
 * factor are pixels/inch, since the unit in ClosetPro 3D scenes is always an
 * inch. This assumes that the camera is not distorted, i.e. that it's aspect
 * ratio is the same as the aspect ratio of the canvas.
 */
export const calcOrthoScale = (camera: OrthographicCamera, canvas: HTMLCanvasElement) => {
    const
        units = (camera.right - camera.left) / camera.zoom,
        pixels = canvas.width;
    return pixels / units;
}


/**
 * The ScreenPoint class is an empty Object3D instance that can be added to a
 * three.js scene to track a poisition. You can interrogate its on-screen
 * position with the toScreen method, passing a camera and the width and height
 * of the viewport.
 * @param args - The arguments are passed to the Object3D constructor.
 */
export class ScreenPoint extends Object3D {
    private worldVec = new Vector3();
    private ndcVec = new Vector3();

    constructor(){
        super();
        this.name = 'ScreenPoint';
    }

    /**
     * Get the screen coordinates of the object as a Vector2.
     * @param width - The width of the viewport in pixels.
     * @param height - The height of the viewport in pixels.
     * @param camera - A camera instance, most likely the active
     * camera in the scene; the one that's doing the rendering.
     * @returns This will be a reference to the vec parameter, or a new Vector2
     * if vec is not provided.
     */
    toScreen(width: number, height: number, camera: Camera, vec: Vector2 = null): Vector2 {
        if(vec == null){
            vec = new Vector2();
        }

        // this._worldVec.set(0, 0, 0);
        // this.localToWorld(this._worldVec);
        this.getWorldPosition(this.worldVec);
        w2s(camera, this.worldVec, this.ndcVec);
        indc(this.ndcVec.x, this.ndcVec.y,
            width, height,
            vec
        );

        return vec;
    }
}

/**
 * Gets the scaled dimensions of the object. Note this may be different from
 * the dimensions (and orientation) of the underlying geometry.
 */
export const dimensions = (obj: Object3D, out?: Vector3): Vector3 => {
    if(!out) out = new Vector3();
    if(obj == null) return out;

    if(obj.userData.dimensions == null) {
        obj.userData.dimensions = new Vector3();
        const box = new Box3();
        box.setFromObject(obj, true);
        obj.userData.dimensions.set(
            box.max.x - box.min.x,
            box.max.y - box.min.y,
            box.max.z - box.min.z);
    }

    out.copy(obj.userData.dimensions);

    return out;
}

export const align = (obj: Object3D, x: number, y: number, z: number) => {
    const
        box = new Box3(),
        center = new Vector3();
    box.setFromObject(obj, true);
    const
        width = box.max.x - box.min.x,
        height = box.max.y - box.min.y,
        depth = box.max.z - box.min.z;

    obj.getWorldPosition(center);

    center.sub(box.min);

    obj.position.set(
        center.x - x * width,
        center.y - y * height,
        center.z - z * depth);
}
